import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, Shield, CheckCircle } from "lucide-react";

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // For request_id flow (scanner-resistant)
  const [requestId, setRequestId] = useState<string | null>(null);
  
  // For legacy token flow (stored but not redeemed until submit)
  const [tokenHash, setTokenHash] = useState<string | null>(null);
  const [tokenType, setTokenType] = useState<string | null>(null);
  
  // For already-authenticated flow
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    // Check for error in URL hash (from failed Supabase redirects)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const errorCode = hashParams.get('error_code');
    const errorDesc = hashParams.get('error_description');
    
    if (errorCode === 'otp_expired' || errorDesc?.includes('expired')) {
      setErrorMessage('This link has expired. Please contact support for a new password setup link.');
      setIsCheckingSession(false);
      return;
    }

    // Check which flow we're using
    const rid = searchParams.get('request_id');
    const token = searchParams.get('token_hash') || searchParams.get('token');
    const type = searchParams.get('type');

    if (rid) {
      // New scanner-resistant flow: request_id in URL
      setRequestId(rid);
      setIsReady(true);
      setIsCheckingSession(false);
      return;
    }

    if (token && type === 'recovery') {
      // Token flow: store token but don't redeem yet
      setTokenHash(token);
      setTokenType(type);
      setIsReady(true);
      setIsCheckingSession(false);
      return;
    }

    // Check for existing session (user already authenticated)
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setHasSession(true);
          setIsReady(true);
        } else {
          // Legacy implicit flow: tokens in hash
          const accessToken = hashParams.get('access_token');
          const hashType = hashParams.get('type');

          if (accessToken && hashType === 'recovery') {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || '',
            });

            if (!error) {
              setHasSession(true);
              setIsReady(true);
            } else {
              setErrorMessage('This link is invalid or has expired.');
            }
          } else {
            setErrorMessage('Invalid password setup link. Please check your email for the correct link.');
          }
        }
      } catch (err) {
        console.error("Error checking session:", err);
        setErrorMessage('An error occurred. Please try again.');
      } finally {
        setIsCheckingSession(false);
      }
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setHasSession(true);
        setIsReady(true);
        setIsCheckingSession(false);
      }
    });

    checkSession();

    return () => subscription.unsubscribe();
  }, [searchParams]);

  const validatePassword = (pwd: string) => {
    const minLength = pwd.length >= 8;
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasLowercase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    return { minLength, hasUppercase, hasLowercase, hasNumber, isValid: minLength && hasUppercase && hasLowercase && hasNumber };
  };

  const passwordValidation = validatePassword(password);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!passwordValidation.isValid) {
      toast.error("Please meet all password requirements");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    
    try {
      if (requestId) {
        // Request ID flow: call edge function
        const { data, error } = await supabase.functions.invoke('set-password', {
          body: { request_id: requestId, password }
        });

      if (error) {
        let friendlyMessage = 'Failed to set password';
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errorBody = await error.context.json();
            friendlyMessage = errorBody?.error || friendlyMessage;
          }
        } catch {
          // Fall back to generic message
        }
        throw new Error(friendlyMessage);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

        toast.success("Password set successfully! Welcome to Wealthy Steward!");
        
        // Sign in the user
        if (data?.email) {
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: data.email,
            password
          });
          
          if (signInError) {
            console.error('Auto sign-in failed:', signInError);
            navigate("/login");
            return;
          }
        }
        
        navigate("/confirm-una");
        
      } else if (tokenHash && tokenType) {
        // Token flow: redeem token NOW (on submit, not on page load)
        const { data: verifyData, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'recovery',
        } as any);

        if (verifyError) {
          throw new Error('This link has expired. Please contact support for a new link.');
        }

        // Now set the password
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;

        // Update provisioning log
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          await supabase
            .from('user_provisioning_log')
            .update({ 
              status: 'password_set',
              password_set_at: new Date().toISOString()
            })
            .eq('email', user.email.toLowerCase());
        }

        toast.success("Password set successfully! Welcome to Wealthy Steward!");
        navigate("/confirm-una");
        
      } else if (hasSession) {
        // Already authenticated flow
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;

        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          await supabase
            .from('user_provisioning_log')
            .update({ 
              status: 'password_set',
              password_set_at: new Date().toISOString()
            })
            .eq('email', user.email.toLowerCase());
        }

        toast.success("Password set successfully! Welcome to Wealthy Steward!");
        navigate("/confirm-una");
        
      } else {
        throw new Error('No valid authentication method available');
      }
      
    } catch (error: any) {
      console.error("Error setting password:", error);
      toast.error(error.message || "Failed to set password");
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="animate-pulse text-muted-foreground">Verifying your access link...</div>
      </div>
    );
  }

  if (errorMessage || !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Shield className="h-12 w-12 mx-auto text-destructive mb-4" />
            <CardTitle>Invalid or Expired Link</CardTitle>
            <CardDescription>
              {errorMessage || 'This password setup link is invalid or has expired. Please check your email for a new link or contact support.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full" 
              onClick={() => navigate("/login")}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Welcome to Wealthy Steward!</CardTitle>
          <CardDescription>
            Set your password to access your stewardship portal and begin your journey.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Create Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="text-sm space-y-1">
              <div className={`flex items-center gap-2 ${passwordValidation.minLength ? 'text-green-600' : 'text-muted-foreground'}`}>
                <CheckCircle className={`h-4 w-4 ${passwordValidation.minLength ? 'opacity-100' : 'opacity-30'}`} />
                At least 8 characters
              </div>
              <div className={`flex items-center gap-2 ${passwordValidation.hasUppercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                <CheckCircle className={`h-4 w-4 ${passwordValidation.hasUppercase ? 'opacity-100' : 'opacity-30'}`} />
                One uppercase letter
              </div>
              <div className={`flex items-center gap-2 ${passwordValidation.hasLowercase ? 'text-green-600' : 'text-muted-foreground'}`}>
                <CheckCircle className={`h-4 w-4 ${passwordValidation.hasLowercase ? 'opacity-100' : 'opacity-30'}`} />
                One lowercase letter
              </div>
              <div className={`flex items-center gap-2 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-muted-foreground'}`}>
                <CheckCircle className={`h-4 w-4 ${passwordValidation.hasNumber ? 'opacity-100' : 'opacity-30'}`} />
                One number
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-destructive">Passwords do not match</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !passwordValidation.isValid || password !== confirmPassword}
            >
              {isLoading ? "Setting Password..." : "Enter the Portal"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPassword;
